import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Wind, TrendingUp, AlertTriangle, Loader2, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const TEST_LABELS = { YOYO: 'Yo-Yo', GACON: 'Gacon' };

export default function AthleteAerobicPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get('/aerobic/my-results').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [api]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  if (!data || data.total_tests === 0) {
    return (
      <div className="text-center py-16" data-testid="athlete-aerobic-empty">
        <Wind className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-500">{t('No aerobic test results yet', 'Nessun risultato aerobico')}</p>
      </div>
    );
  }

  const { latest, history, total_tests, alerts } = data;

  const chartData = {};
  for (const [tt, items] of Object.entries(history)) {
    chartData[tt] = items.filter(r => r.date).sort((a, b) => a.date.localeCompare(b.date)).map(r => ({
      date: r.date, mas: r.mas_vam_kmh, distance: r.total_distance_m,
    }));
  }

  return (
    <div className="space-y-6 fade-in" data-testid="athlete-aerobic-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
          <Wind className="w-6 h-6 text-teal-400" /> {t('Aerobic Testing', 'Test Aerobici')}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">{t('Your aerobic performance results', 'I tuoi risultati aerobici')}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <Activity className="w-5 h-5 text-teal-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-zinc-100" data-testid="my-total">{total_tests}</div>
          <div className="text-xs text-zinc-500">{t('Total Tests', 'Test')}</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <TrendingUp className="w-5 h-5 text-teal-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-teal-400" data-testid="my-best-mas">
            {latest.YOYO?.mas_vam_kmh || latest.GACON?.mas_vam_kmh || '—'}
          </div>
          <div className="text-xs text-zinc-500">{t('Latest MAS (km/h)', 'MAS Ultimo')}</div>
        </CardContent></Card>
      </div>

      {alerts.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardHeader className="pb-2"><CardTitle className="text-amber-400 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {t('Notifications', 'Notifiche')}
          </CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className="text-sm p-2 bg-amber-500/5 rounded-sm text-zinc-300">
                {a.type === 'PERFORMANCE_DROP' && t(`MAS decreased by ${a.drop_pct}%`, `MAS calata del ${a.drop_pct}%`)}
                {a.type === 'BELOW_TEAM_AVG' && t(`${a.below_pct}% below team average`, `${a.below_pct}% sotto la media`)}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2"><CardTitle className="text-zinc-100 text-base">{t('Latest Results', 'Ultimi')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(latest).filter(([_, d]) => d).map(([tt, d]) => (
            <div key={tt} className="flex items-center justify-between p-3 rounded-sm border border-zinc-800 bg-zinc-950/50">
              <div>
                <div className="text-sm font-semibold text-teal-400">{TEST_LABELS[tt]} {d.protocol_name ? `(${d.protocol_name})` : ''}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{d.date}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-zinc-100">{d.mas_vam_kmh} km/h</div>
                <div className="text-xs text-zinc-400">{d.total_distance_m}m</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {Object.entries(chartData).filter(([_, d]) => d.length > 1).map(([tt, cdata]) => (
        <Card key={tt} className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-zinc-100 text-base">{TEST_LABELS[tt]} — MAS/VAM {t('Trend', 'Andamento')}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={cdata}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ backgroundColor: '#1c1c1c', border: '1px solid #333' }} />
                <Line type="monotone" dataKey="mas" stroke="#2dd4bf" strokeWidth={2} dot={{ fill: '#2dd4bf', r: 3 }} name="MAS km/h" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
